import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalOption } from './useGoalSelectOptions';
import { useUserInfo } from '../../../context/UserInfo';
import { GoalId } from './types';

const AVAILABLE_GOALS_FILTER_MODES = [
  'companyAndTeam',
  'allExceptMy',
  'all',
] as const;

export type AvailableGoalsFilterMode =
  (typeof AVAILABLE_GOALS_FILTER_MODES)[number];
const isAvailableGoalsFilterMode = (
  v: string | null,
): v is AvailableGoalsFilterMode =>
  AVAILABLE_GOALS_FILTER_MODES.includes(v as AvailableGoalsFilterMode);

// todo: move to another namespace
const i18prefix = 'performanceReview.goals2.pullGoal.filter';

type Props = {
  allGoals: GoalOption[];
  setSelectedGoalId: React.Dispatch<React.SetStateAction<GoalId | null>>;
};

export const useAvailableGoalsFilter = ({
  allGoals,
  setSelectedGoalId,
}: Props) => {
  const { t } = useTranslation();
  const { id: currentUserId } = useUserInfo();
  const [filterMode, setFilterMode] =
    useState<AvailableGoalsFilterMode>('companyAndTeam');

  const filteredGoals = useMemo(
    () => filterGoalsByMode(allGoals, filterMode, currentUserId),
    [allGoals, filterMode, currentUserId],
  );

  const filterModeOptions = useMemo(
    () =>
      AVAILABLE_GOALS_FILTER_MODES.map((mode) => ({
        label: t(`${i18prefix}.${mode}`),
        value: mode,
      })),
    [t],
  );

  const handleFilterModeChange = useCallback(
    (value: string | null) => {
      const newMode: AvailableGoalsFilterMode = isAvailableGoalsFilterMode(
        value,
      )
        ? value
        : 'companyAndTeam';
      setFilterMode(newMode);
      setSelectedGoalId((prev: GoalId | null) => {
        if (!prev) return null;
        const stillAvailable = filterGoalsByMode(
          allGoals,
          newMode,
          currentUserId,
        ).some((g) => g.value === prev.id);
        return stillAvailable ? prev : null;
      });
    },
    [allGoals, currentUserId, setSelectedGoalId],
  );

  const resetFilterMode = useCallback(() => {
    setFilterMode('companyAndTeam');
  }, []);

  return {
    filterMode,
    filterModeOptions,
    filteredGoals,
    handleFilterModeChange,
    resetFilterMode,
  };
};

export const filterGoalsByMode = (
  goals: GoalOption[],
  mode: AvailableGoalsFilterMode,
  currentUserId: string,
): GoalOption[] => {
  switch (mode) {
    case 'companyAndTeam':
      return goals.filter(
        (g) => g.goalType === 'company' || g.goalType === 'team',
      );
    case 'allExceptMy':
      return goals.filter(
        (g) => !g.goalEmployee || g.goalEmployee.id !== currentUserId,
      );
    case 'all':
    default:
      return goals;
  }
};
